import { Injectable } from '@angular/core';
import {
    SurveyUserTypes,
    AssessmentType,
    SendAssessmentFlowType
} from '../constants-types/Enums';
import { BehavioralAssessmentDetailsServer } from '../core-data-model';
import { InvitationSource } from '../core-data-model/Assessment.model';
import { AssessmentDetail } from '../core-data-model/AssessmentDetails.model';
import {
    BehavioralAssessmentServer,
    CognitiveAssessmentServer,
    CompleteBehavioralAssessmentServer,
    CreateBARequestPayload,
    RecentBehavioralAssessmentServerEntry,
    SimulatedBehavioralAssessmentServer
} from '../core-data-model/AssessmentsApi.serverinterface';
import { BehavioralAssessment } from '../core-data-model/BehavioralAssessment.model';
import { CognitiveAssessment } from '../core-data-model/CognitiveAssessment.model';
import { GuidebookServer } from '../core-data-model/Guidebook.interface';
import { Job } from '../core-data-model/Job.model';
import {
    FullPattern,
    Pattern,
    CustomPatternTitle
} from '../core-data-model/Pattern.model';
import { PatternInsights } from '../core-data-model/PatternInsights.interface';
import { Person, PersonUpdate } from '../core-data-model/Person.model';
import { ScheduledAssessmentDetails } from '../core-data-model/ScheduledAssessmentDetails.model';
import { FoldersDataService } from '../core-services/Folders.dataservice';
import { GuidebookTransformers } from '../core-services/Guidebook.datatransform';
import {
    PatternTransformer,
    PatternInputData
} from '../core-services/Pattern.datatransform';
import { ToolboxTransformer } from '../core-services/Toolbox.datatransform';
import { DateFormatUtility } from '../core-utilities/DateFormat.utility';
import { Util } from '../core-utilities/Util';
import { BehavioralAssessmentFactory } from '../object-factory/BehavioralAssessment.factory';
import { InitDataService } from '../system-services';
import { TranslationService } from '../translations';

@Injectable({
    providedIn: 'root'
})
export class AssessmentsTransformers {
    constructor(
        private readonly folderService: FoldersDataService,
        private readonly util: Util,
        private readonly baFactory: BehavioralAssessmentFactory,
        private readonly guidebookTransformers: GuidebookTransformers,
        private readonly toolboxTransfromers: ToolboxTransformer,
        public readonly dateUtil: DateFormatUtility,
        public readonly TR: TranslationService,
        private readonly initData: InitDataService
    ) {}

    // The stuff around pattern is a data fix because the API Endpoint sends us patterns with 0s instead
    // of sending us a null pattern
    behavioralAssessmentTransform(
        serverJSON: CompleteBehavioralAssessmentServer
    ): BehavioralAssessment {
        return this.baFactory.new_BehavioralAssessment({
            // following is probably not great
            administeredBy: Person.fullName({
                firstName: serverJSON.administeredByFirstName,
                lastName: serverJSON.administeredByLastName
            }),
            completedDate: this.util.newDate(
                serverJSON.assessmentCompletedDateTime
            ),
            customFields: serverJSON.customFields || [],
            expirationDate: this.util.newDate(serverJSON.expirationDateTime),
            sentDate: this.util.newDate(serverJSON.assessmentDateTime),
            id: serverJSON.assessmentId,
            pattern: serverJSON.assessmentCompletedDateTime
                ? PatternTransformer.transformToFullPattern(
                      serverJSON,
                      serverJSON.scoreId
                  )
                : null,
            patternNumber: serverJSON.patternNumber,
            scoringMethod: serverJSON.scoringMethod,
            type: AssessmentType.Behavioral,
            assessmentState: serverJSON.assessmentState
        });
    }

    recentBehavioralAssessmentTransform(
        item: RecentBehavioralAssessmentServerEntry
    ): Person {
        return new Person({
            firstName: item.assessmentUser.firstName,
            middleName: item.assessmentUser.middleName,
            lastName: item.assessmentUser.lastName,
            email: item.assessmentUser.email,
            type: item.assessmentUser.personType,
            id: item.assessmentUser.assessmentUserId,
            folder: this.folderService.getFolder(item.assessmentUser.folderId),
            behavioralAssessments: item.assessmentInformation
                ? [
                      this.baFactory.new_BehavioralAssessment({
                          administeredBy:
                              item.assessmentInformation.administeredBy,
                          administeredById:
                              item.assessmentInformation.administeredById,
                          completedDate: this.util.newDate(
                              item.assessmentInformation.completedDateTime
                          ),
                          sentDate: this.util.newDate(
                              item.assessmentInformation.assessmentSentDate
                          ),
                          id: item.assessmentInformation.assessmentId,
                          assessmentState:
                              item.assessmentInformation.assessmentState,
                          pattern: new FullPattern({
                              self: item.assessmentInformation.selfSigmaScore
                                  ? new Pattern({
                                        A: item.assessmentInformation
                                            .selfSigmaScore.aSigma,
                                        B: item.assessmentInformation
                                            .selfSigmaScore.bSigma,
                                        C: item.assessmentInformation
                                            .selfSigmaScore.cSigma,
                                        D: item.assessmentInformation
                                            .selfSigmaScore.dSigma,
                                        E: item.assessmentInformation
                                            .selfSigmaScore.eSigma,
                                        M: item.assessmentInformation
                                            .selfSigmaScore.mScore
                                    })
                                  : null
                          }),
                          // mock is used temporarily to use BehavioralAsessment despite the 4 missing properties in backend
                          // the type should be Person --> PartialPerson and
                          // BehavioralAsessment --> BehavioralAsessmnetDetail
                          // to account for hte fact that the endpoint is not sending full properties
                          // expected in Person or BehavioralAsessment
                          patternNumber: item.referencePatternInformation
                              ? item.referencePatternInformation.patternNumber
                              : null,
                          assessmentConsent: 'Unknown',
                          scoreId: '0',
                          customFields: []
                          // mock ends
                      })
                  ]
                : [],
            cognitiveAssessments: item.highestCognitiveAssessment
                ? [
                      new CognitiveAssessment({
                          administeredBy:
                              item.highestCognitiveAssessment.administeredBy,
                          administeredById:
                              item.highestCognitiveAssessment.administeredById,
                          assessmentState: item.highestCognitiveAssessment
                              .assessmentState
                              ? item.highestCognitiveAssessment.assessmentState
                              : SendAssessmentFlowType.Send,
                          id: item.highestCognitiveAssessment.id,
                          completedDate: this.util.newDate(
                              item.highestCognitiveAssessment
                                  .assessmentCompletedDateTime
                          ),
                          sentDate: this.util.newDate(
                              item.highestCognitiveAssessment.assessmentSentDate
                          ),
                          score: item.highestCognitiveAssessment.score
                      })
                  ]
                : [],
            job: new Job({
                id: item.jobInformation.id,
                title: item.jobInformation.name
            })
        });
    }

    sendAssessmentDataTransformation(
        scheduledAssessmentDetails: ScheduledAssessmentDetails,
        isCognitive: boolean,
        invitiationSource: InvitationSource,
        skipNotifyAnalystCompletionEmail?: boolean,
        expirationDateOverride?: Date,
        autoSendBehavioralReport = false,
        sendCognitiveAfterBehavioralIsComplete = false,
        skipNotifyAssessment = false
    ) {
        return scheduledAssessmentDetails.assessments.map(
            (assessment: AssessmentDetail) => {
                assessment.autoSendBehavioralReport = autoSendBehavioralReport;
                return this.processAssessment(
                    assessment,
                    scheduledAssessmentDetails,
                    isCognitive,
                    sendCognitiveAfterBehavioralIsComplete,
                    invitiationSource,
                    skipNotifyAssessment,
                    skipNotifyAnalystCompletionEmail,
                    expirationDateOverride
                );
            }
        );
    }

    private processAssessment(
        assessment: AssessmentDetail,
        scheduledAssessmentDetails: ScheduledAssessmentDetails,
        isCognitive: boolean,
        sendCognitiveAfterBehavioralIsComplete: boolean,
        invitationSource: InvitationSource,
        skipNotifyAssessment: boolean,
        skipNotifyAnalystCompletionEmail = false,
        expirationDateOverride?: Date
    ) {
        // Setting Most Recently Used Folder
        this.folderService.updateMostRecentFolders(assessment.person.folder.id);

        const assessmentData: any = {
            subject: isCognitive
                ? scheduledAssessmentDetails.cognitiveEmail.subject
                : scheduledAssessmentDetails.behavioralEmail.subject,
            message: isCognitive
                ? scheduledAssessmentDetails.cognitiveEmail.message
                : scheduledAssessmentDetails.behavioralEmail.message,
            expirationDateTime:
                expirationDateOverride ??
                scheduledAssessmentDetails.expirationDateTime,
            administeredByEmail: isCognitive
                ? scheduledAssessmentDetails.cognitiveEmail.administrator.email
                : scheduledAssessmentDetails.behavioralEmail.administrator
                      .email,
            administeredByPhone: isCognitive
                ? scheduledAssessmentDetails.cognitiveEmail.administrator.phone
                : scheduledAssessmentDetails.behavioralEmail.administrator
                      .phone,
            administeredByFirstName: isCognitive
                ? scheduledAssessmentDetails.cognitiveEmail.administrator
                      .firstName
                : scheduledAssessmentDetails.behavioralEmail.administrator
                      .firstName,
            administeredByLastName: isCognitive
                ? scheduledAssessmentDetails.cognitiveEmail.administrator
                      .lastName
                : scheduledAssessmentDetails.behavioralEmail.administrator
                      .lastName,
            gender: null,
            personType: SurveyUserTypes[assessment.person.type],
            assessmentStatusWebHook: null,
            notifyAssessmentAvailableUsingEmail: skipNotifyAssessment ? 0 : 1,
            externalId: null,
            externalPersonId: null,
            languageLocale: isCognitive
                ? scheduledAssessmentDetails.cognitiveEmail.language.locale
                : scheduledAssessmentDetails.behavioralEmail.language.locale,
            redirectURL: null,
            sendCognitiveAssessmentOnCompletion:
                sendCognitiveAfterBehavioralIsComplete ? 1 : 0,
            scoreId: scheduledAssessmentDetails.behavioralScoreId,
            assessmentDateForScoreId:
                scheduledAssessmentDetails.assessmentDateForScoreId,
            invitationSource: invitationSource,
            sendAnalystCompletionEmail: skipNotifyAnalystCompletionEmail ? 0 : 1
        };

        if (isCognitive) {
            assessmentData.administeredByCompanyName =
                scheduledAssessmentDetails.cognitiveEmail.administrator.company;
        } else {
            assessmentData.customFields = [];
            assessmentData.autoSendPIReportToAssessmentTaker =
                assessment.autoSendBehavioralReport ? 1 : 0;
            assessmentData.internalUse = '1'; // indicate to use the new email template
        }

        if (assessment.person.id) {
            assessmentData.assessmentUserGuid = assessment.person.id;
        } else {
            assessmentData.firstName = assessment.person.firstName;
            assessmentData.middleName = assessment.person.middleName;
            assessmentData.lastName = assessment.person.lastName;
            assessmentData.email = assessment.person.email;
            assessmentData.folderGuid = assessment.person.folder.id;
            assessmentData.associatedPositionTitle = assessment.job
                ? assessment.job.title
                : null;
        }
        return assessmentData;
    }

    processBehavioralAssessment(
        server: BehavioralAssessmentServer
    ): BehavioralAssessment {
        return this.baFactory.new_BehavioralAssessment({
            administeredBy: server.administeredBy,
            completedDate: this.util.newDate(
                server.assessmentCompletedDateTime
            ),
            sentDate: this.util.newDate(server.assessmentDateTime),
            expirationDate: this.util.newDate(server.expirationDateTime),
            id: server.assessmentId,
            // INCORRECT MAPPING: NEEDS DISCUSSION
            pattern: PatternTransformer.transformToFullPattern(server),
            patternNumber: server.patternNumber,
            scoringMethod: server.scoringMethod,
            assessmentState: server.assessmentState
        });
    }

    processCognitiveAssessment(
        server: CognitiveAssessmentServer
    ): CognitiveAssessment {
        return new CognitiveAssessment({
            id: server.assessmentId,
            score: server.score,
            completedDate: this.util.newDate(
                server.assessmentCompletedDateTime
            ),
            sentDate: this.util.newDate(server.assessmentSentDateTime),
            assessmentState: server.assessmentState,

            administeredBy: server.administeredBy,

            // Meta Info
            attempted: server.attempted,
            correct: server.correct,
            percentileRank: server.percentile
        });
    }

    transformPatternInsights(server: GuidebookServer): PatternInsights {
        return {
            factorInfo: server.guidebook.factors.map((entry) =>
                this.guidebookTransformers.transformFactorInfo(entry)
            ),
            factorCombinationInfo:
                server.guidebook.factorEmphasisCombination.factorCombinations.map(
                    (entry) => ({
                        primaryTrait: entry.primaryTrait,
                        traits: entry.traits,
                        type: entry.type,
                        midpointRelationship: entry.midpointRelationship,
                        factorDifference: entry.factorDifference
                    })
                ),
            strongestBehaviorContents: server.strongestBehaviorContents,
            referenceProfileInfo:
                this.toolboxTransfromers.transformToRefProfile(
                    server.guidebook.referencePattern
                )
        };
    }

    transformSimulatedBehavioralPattern(
        server: SimulatedBehavioralAssessmentServer
    ): BehavioralAssessment {
        const patternInputData: PatternInputData = {
            selfSigmaScore: server.selfSigmaScores,
            selfConceptSigmaScore: server.SelfConceptSigmaScores,
            synthesisSigmaScore: server.SynthesisSigmaScores
        };
        const fullPatern =
            PatternTransformer.transformToFullPattern(patternInputData);
        const customTitle: CustomPatternTitle = {
            self: this.dateUtil.transformDate(
                new Date(server.selfAssessmentDateTime)
            ),
            selfConcept: this.dateUtil.transformDate(
                new Date(server.selfConceptAssessmentDateTime)
            ),
            synthesis: this.TR.key('label.common-terms.calculated')
        };
        fullPatern.customTitle = customTitle;
        return this.baFactory.new_BehavioralAssessment({
            pattern: fullPatern,
            completedDate: new Date(server.selfAssessmentDateTime)
        });
    }

    transformBehavioralAssessmentServerToPerson(
        baServer: CompleteBehavioralAssessmentServer
    ): Person {
        const behavioralAssessment =
            this.behavioralAssessmentTransform(baServer);

        const person = new Person({
            id: baServer.assessmentUserId,
            firstName: baServer.firstName,
            middleName: baServer.middleName,
            lastName: baServer.lastName,
            email: baServer.email,
            behavioralAssessments: [behavioralAssessment],
            lastCompleted: this.util.newDate(
                baServer.assessmentCompletedDateTime
            ),
            lastSent: this.util.newDate(baServer.assessmentSentDateTime),
            type: Number(baServer.personType) as SurveyUserTypes
            // BA information contains no info on lastUpdated SU, CA, folder, or job
            // so these fields will not be populated
        });

        return person;
    }

    transformCreateBARequestPayload(
        person: PersonUpdate,
        invitationSource: InvitationSource,
        sendBAReportToAssessmentTaker: boolean,
        companyGuid: string
    ): CreateBARequestPayload {
        return {
            companyId: companyGuid,
            firstName: person.firstName,
            lastName: person.lastName,
            email: person.email,
            personType: person.type.toString(),
            folderGuid: person.folder.id,
            administeredById: this.initData.user.userGuid,
            analystEmail: this.initData.user.email,
            analystFirstName: this.initData.user.firstName,
            analystLastName: this.initData.user.lastName,
            analystUserId: this.initData.user.userGuid,
            notifyAssessmentAvailableUsingEmail: 1,
            assessmentUserGuid: person.originalObject?.id
                ? person.originalObject.id
                : null,
            autoSendPIReportToAssessmentTaker: sendBAReportToAssessmentTaker
                ? 1
                : 0,
            customFields: [],
            sendAnalystCompletionEmail: 1,
            invitationSource: invitationSource,
            jobId:
                person.job && person.job !== 'RemoveJob' ? person.job.id : null
        };
    }

    transformPersonFromPost(
        serverData: BehavioralAssessmentDetailsServer,
        originalPerson: PersonUpdate
    ): Person {
        return new Person({
            ...originalPerson,
            job:
                typeof originalPerson.job === 'string'
                    ? null
                    : originalPerson.job,
            id: serverData.assessmentUserId,
            firstName: serverData.firstName,
            lastName: serverData.lastName,
            email: serverData.email,
            folder: originalPerson.folder
        });
    }
}
